import React, { useEffect, useState } from "react";
import QRCode from "qrcode";
import { Printer, ShieldAlert, FileSearch, Scale, Landmark, Banknote, Phone, Globe, Mail, AlertTriangle } from "lucide-react";
import { FIRM } from "@/lib/firm";

const HERO = "https://images.unsplash.com/photo-1635859890085-ec8cb5466806?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const FLYER_CSS = `
  .flyer-wrap{background:#0a140f;min-height:100vh;display:flex;flex-direction:column;align-items:center;padding:28px 16px;}
  .flyer{width:8.5in;min-height:11in;background:#faf7ef;color:#12241c;box-shadow:0 20px 60px rgba(0,0,0,.5);
    display:flex;flex-direction:column;overflow:hidden;font-family:'Manrope',system-ui,sans-serif;position:relative;}
  .fl-hero{position:relative;height:3.05in;background:#0B3D2E;overflow:hidden;}
  .fl-hero img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;opacity:.34;}
  .fl-hero-shade{position:absolute;inset:0;background:linear-gradient(120deg,#05130d 10%,rgba(6,48,36,.6) 65%,rgba(6,48,36,.25));}
  .fl-hero-inner{position:relative;padding:34px 40px;color:#F3EFE0;height:100%;display:flex;flex-direction:column;justify-content:center;}
  .fl-eyebrow{display:inline-flex;align-items:center;gap:8px;font-size:11px;letter-spacing:.24em;text-transform:uppercase;color:#E4C97A;font-weight:800;}
  .fl-brand{font-size:13px;letter-spacing:.1em;color:#cfe0d7;margin-top:4px;text-transform:uppercase;font-weight:700;}
  .fl-h1{font-family:Georgia,'Times New Roman',serif;font-size:39px;line-height:1.03;margin:12px 0 8px;color:#fff;max-width:6in;font-weight:700;}
  .fl-sub{font-size:14.5px;line-height:1.5;color:#e7f3ee;max-width:5.5in;}
  .fl-gold-rule{height:5px;background:linear-gradient(90deg,#E4C97A,#B8860B 55%,#E4C97A);}
  .fl-body{padding:24px 40px 10px;display:flex;flex-direction:column;flex:1;}
  .fl-lead{font-size:14px;line-height:1.55;color:#24382f;}
  .fl-lead b{color:#0B3D2E;}
  .fl-benefits{display:grid;grid-template-columns:1fr 1fr;gap:13px 26px;margin:18px 0 6px;}
  .fl-ben{display:flex;gap:12px;align-items:flex-start;}
  .fl-ben .ic{flex:0 0 38px;height:38px;border-radius:10px;background:#0B3D2E;color:#E4C97A;display:flex;align-items:center;justify-content:center;}
  .fl-ben .tt{font-size:14px;font-weight:800;color:#0B3D2E;}
  .fl-ben .dd{font-size:12px;line-height:1.45;color:#415349;margin-top:2px;}
  .fl-signs{background:#f3ecd9;border:1px solid #e3d9ba;border-radius:12px;padding:14px 18px;margin:6px 0 2px;}
  .fl-signs .hd{font-size:11px;letter-spacing:.18em;text-transform:uppercase;color:#8a5a12;font-weight:800;display:flex;align-items:center;gap:7px;margin-bottom:7px;}
  .fl-signs ul{margin:0;padding-left:0;list-style:none;display:grid;grid-template-columns:1fr 1fr;gap:4px 18px;}
  .fl-signs li{font-size:12px;color:#3a3020;line-height:1.4;display:flex;gap:7px;}
  .fl-signs li::before{content:"";flex:0 0 6px;height:6px;margin-top:6px;border-radius:50%;background:#B8860B;}
  .fl-cta{margin-top:auto;display:flex;gap:24px;align-items:center;background:#0B3D2E;border-radius:14px;padding:20px 24px;color:#F3EFE0;}
  .fl-qr{flex:0 0 auto;background:#fff;padding:12px;border-radius:12px;line-height:0;box-shadow:0 6px 20px rgba(0,0,0,.25);}
  .fl-qr img{width:1.55in;height:1.55in;display:block;}
  .fl-cta-txt{flex:1;}
  .fl-scan{font-size:13px;letter-spacing:.22em;text-transform:uppercase;color:#E4C97A;font-weight:800;}
  .fl-cta-h{font-family:Georgia,serif;font-size:25px;margin:4px 0 6px;color:#fff;}
  .fl-url{font-size:13px;color:#cfe0d7;word-break:break-all;}
  .fl-fee{display:inline-flex;gap:14px;align-items:baseline;margin-top:10px;background:rgba(228,201,122,.14);border:1px solid rgba(228,201,122,.5);border-radius:10px;padding:8px 14px;}
  .fl-fee .amt{font-family:Georgia,serif;font-size:22px;color:#E4C97A;font-weight:700;}
  .fl-fee .mo{font-size:12px;color:#e7f3ee;}
  .fl-pay{font-size:11.5px;line-height:1.5;color:#e7f3ee;margin-top:10px;}
  .fl-pay b{color:#E4C97A;}
  .fl-foot{padding:14px 40px 22px;border-top:1px solid #e3ddc9;}
  .fl-contact{display:flex;gap:22px;flex-wrap:wrap;font-size:12.5px;color:#2c4036;font-weight:700;align-items:center;}
  .fl-contact span{display:inline-flex;gap:6px;align-items:center;}
  .fl-disc{font-size:9px;line-height:1.5;color:#7a8a81;margin-top:8px;}
  .fl-print-btn{margin:0 0 18px;display:inline-flex;gap:8px;align-items:center;background:#E4C97A;color:#0a140f;border:none;border-radius:999px;padding:12px 26px;font-weight:800;font-size:13px;letter-spacing:.06em;text-transform:uppercase;cursor:pointer;}
  @media print{
    @page{size:letter;margin:0;}
    body{background:#fff!important;}
    .no-print{display:none!important;}
    .flyer-wrap{padding:0!important;background:#fff!important;}
    .flyer{box-shadow:none!important;width:100%!important;min-height:100vh!important;}
    .fl-hero img{opacity:.34!important;-webkit-print-color-adjust:exact;print-color-adjust:exact;}
    .flyer,.fl-hero,.fl-cta,.fl-gold-rule,.fl-ben .ic,.fl-fee,.fl-signs{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;}
  }
`;

const Ben = ({ icon: Icon, tt, dd }) => (
  <div className="fl-ben"><span className="ic"><Icon size={19} /></span><div><div className="tt">{tt}</div><div className="dd">{dd}</div></div></div>
);

export default function FciuFlyer() {
  const url = "https://www.seutrading.com/financial-crimes-unit";
  const [qr, setQr] = useState("");

  useEffect(() => {
    QRCode.toDataURL(url, { margin: 1, width: 320, errorCorrectionLevel: "M",
      color: { dark: "#0B3D2E", light: "#ffffff" } })
      .then(setQr).catch(() => setQr(""));
  }, [url]);

  return (
    <div className="flyer-wrap">
      <button className="fl-print-btn no-print" onClick={() => window.print()} data-testid="fciu-flyer-print">
        <Printer size={16} /> Print / Save as PDF
      </button>

      <div className="flyer" data-testid="fciu-flyer">
        <div className="fl-hero">
          <img src={HERO} alt="Reviewing documents" crossOrigin="anonymous" />
          <div className="fl-hero-shade" />
          <div className="fl-hero-inner">
            <div className="fl-eyebrow"><ShieldAlert size={14} /> Financial Crimes Investigations Unit</div>
            <div className="fl-brand">{FIRM.name}</div>
            <h1 className="fl-h1">Were you scammed,<br />defrauded or cheated out of money?</h1>
            <p className="fl-sub">We help victims of financial fraud organize the evidence and file the right complaints — with the FBI, FTC, SEC, FINRA, your bank and law enforcement — to pursue recovery.</p>
          </div>
        </div>
        <div className="fl-gold-rule" />

        <div className="fl-body">
          <p className="fl-lead">
            If a lender, broker, "investor" or company took your money and disappeared — or never delivered what they promised — our
            <b> Financial Crimes Investigations Unit</b> prepares a complete <b>Fraud Report &amp; Evidence Packet</b>, a sworn affidavit, and
            ready‑to‑send demand, wire‑recall and chargeback letters, then <b>submits your complaints to the correct agencies on your behalf</b>.
          </p>

          <div className="fl-benefits">
            <Ben icon={FileSearch} tt="Evidence Packet Assembled" dd="Your facts, documents and exhibits organized into a professional, agency‑ready report." />
            <Ben icon={Scale} tt="Sworn Affidavit & Letters" dd="Penalty‑of‑perjury affidavit plus demand, cease‑and‑desist, wire‑recall & chargeback letters." />
            <Ben icon={Landmark} tt="Filed With the Right Agencies" dd="FBI IC3, FTC, SEC, FINRA, CFPB, IRS‑CI, state AG, local police — submitted for you." />
            <Ben icon={Banknote} tt="Bank Recovery Coordination" dd="Time‑sensitive wire‑recall / ACH‑dispute steps to help your bank claw funds back." />
          </div>

          <div className="fl-signs">
            <div className="hd"><AlertTriangle size={14} /> You may have been defrauded if…</div>
            <ul>
              <li>You paid an upfront fee for a loan that never funded</li>
              <li>An "investment" or private note stopped paying or vanished</li>
              <li>You were pressured to wire money quickly</li>
              <li>A broker or firm can't be verified anywhere</li>
              <li>Promised documents or title never arrived</li>
              <li>Contact went silent after you paid</li>
            </ul>
          </div>

          <div className="fl-cta">
            <div className="fl-qr">{qr ? <img src={qr} alt="Scan to start your case" data-testid="fciu-flyer-qr" /> : <div style={{ width: "1.55in", height: "1.55in" }} />}</div>
            <div className="fl-cta-txt">
              <div className="fl-scan">Scan to start your case</div>
              <div className="fl-cta-h">Report the fraud &amp; get your packet</div>
              <div className="fl-url">{url}</div>
              <div className="fl-fee"><span className="amt">$850</span><span className="mo">one‑time engagement · complete packet assembled, emailed &amp; filed for you</span></div>
              <div className="fl-pay">Scan the code or visit the link, complete the short intake, then <b>pay securely by card (Stripe)</b>. Your kit is assembled and delivered automatically, with a private <b>case portal</b> to track every filing.</div>
            </div>
          </div>
        </div>

        <div className="fl-foot">
          <div className="fl-contact">
            <span><Phone size={14} color="#0B3D2E" /> {FIRM.phone}</span>
            <span><Globe size={14} color="#0B3D2E" /> www.seutrading.com</span>
            <span><Mail size={14} color="#0B3D2E" /> seutradeinfo@gmail.com</span>
            <span><Landmark size={14} color="#0B3D2E" /> {FIRM.address}</span>
          </div>
          <p className="fl-disc">
            {FIRM.name}'s Financial Crimes Investigations Unit prepares and submits administrative fraud complaints and organizes the
            complainant's own evidence. It is <b>NOT a law firm</b>, provides no legal advice, and does not file lawsuits, represent clients in
            court, or locate, track or surveil any individual. Identifying details are organized solely so that law enforcement and regulators
            may investigate through lawful process. The $850 fee covers preparation and submission of the packet and complaints; it does not
            guarantee recovery of any funds or any particular outcome. No accusation is made that any named person committed a crime.
          </p>
        </div>
      </div>
      <style>{FLYER_CSS}</style>
    </div>
  );
}
